import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    IconButton,
    Typography,
    MenuItem,
    InputAdornment
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import api from "../../api/api";
import { LoadingButton } from "@mui/lab";
import { Visibility, VisibilityOff } from '@mui/icons-material';

import en_ja from "../../translations/en_ja";

const roles = ["Admin"];
const nameRegex = /^[\p{L}\s]+$/u;

const CreateNewAdmin = ({
    open,
    onClose,
    editMode = false,
    onSubmit,
    setSnackbarType,
    setSnackbarOpen,
    setSnackbarMessage,
    adminData
}) => {
    const [formData, setFormData] = useState({
        first_name: "",
        last_name: "",
        name: "",
        email: "",
        password: "",
        role: "",
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [initialData, setInitialData] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);

    const validateForm = () => {
        const newErrors = {};

        // First Name
        if (!formData.first_name.trim()) {
            newErrors.first_name = en_ja["First Name is required"];
        } else if (!nameRegex.test(formData.first_name.trim())) {
            newErrors.first_name = en_ja["Only letters are allowed"];
        }

        // Last Name
        if (!formData.last_name.trim()) {
            newErrors.last_name = en_ja["Last Name is required"];
        } else if (!nameRegex.test(formData.last_name.trim())) {
            newErrors.last_name = en_ja["Only letters are allowed"];
        }

        // Username
        if (!formData.name.trim()) {
            newErrors.name = en_ja["User Name is required"];
        } else if (!nameRegex.test(formData.name.trim())) {
            newErrors.name = en_ja["Only letters are allowed"];
        }

        // Email
        if (!formData.email.trim()) {
            newErrors.email = en_ja["Email is required"];
        }
        else if (!/^[A-Za-z0-9._%+-]+@oaklawn\.co\.jp$/.test(formData.email.trim())) {
            newErrors.email = en_ja["Email must be a valid @oaklawn.co.jp address"];
        }

        // Password
        if (!editMode) {
            if (!formData.password.trim()) {
                newErrors.password = en_ja["Password is required"];
            } else if (formData.password.length < 6) {
                newErrors.password = en_ja["Minimum 6 characters required"];
            }
            // else if (formData.password.length > 10) {
            //     newErrors.password = en_ja["Maximum 10 characters allowed"];
            // }
        }

        // Role
        if (!editMode && !formData.role) {
            newErrors.role = en_ja["Role is required"];
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    useEffect(() => {
        if (editMode && adminData) {
            const data = {
                first_name: adminData.first_name ?? "",
                last_name: adminData.last_name ?? "",
                name: adminData.name ?? "",
                email: adminData.email ?? "",
                password: adminData.password ?? "",
                role: adminData.role ?? "",
            };

            setFormData(data);
            setInitialData(data);
            setHasChanges(false);
        }
    }, [editMode, adminData]);


    useEffect(() => {
        if (!editMode || !initialData) return;

        const changed =
            formData.first_name !== initialData.first_name ||
            formData.last_name !== initialData.last_name ||
            formData.name !== initialData.name ||
            formData.role !== initialData.role;

        setHasChanges(changed);
    }, [formData, initialData, editMode]);


    const handleChange = (e) => {
        const { name, value } = e.target;

        if (["first_name", "last_name", "name"].includes(name)) {
            if (!/^[\p{L}\s]*$/u.test(value)) {
                setErrors((prev) => ({
                    ...prev,
                    [name]: en_ja["Only letters are allowed"],
                }));
            } else {
                setErrors((prev) => ({
                    ...prev,
                    [name]: "",
                }));
            }
        } else {
            setErrors((prev) => ({
                ...prev,
                [name]: "",
            }));
        }

        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleClose = () => {
        setFormData({
            first_name: "",
            last_name: "",
            name: "",
            email: "",
            password: "",
            role: "",
        });
        setErrors({});
        onClose && onClose();
    };

    const handleSubmit = async () => {

        if (editMode && !hasChanges) return;

        if (!validateForm()) return;

        setLoading(true);

        const payload = {
            name: formData.name.trim(),
            email: formData.email.trim(),
            password: formData.password.trim(),
            first_name: formData.first_name.trim(),
            last_name: formData.last_name.trim(),
            role: formData.role,
        };

        try {
            let response;

            if (editMode) {
                response = await api.put(
                    `/v1/admin/edit/profile/${adminData.id}`,
                    payload
                );
            } else {
                response = await api.post("/v1/admin/create", payload);
            }

            if (response.status === 200 || response.status === 201) {
                setSnackbarMessage(
                    editMode
                        ? en_ja["Admin updated successfully"]
                        : en_ja["Admin created successfully"]
                );
                setSnackbarOpen(true);
                setSnackbarType("success");
                onSubmit && onSubmit();
                handleClose();
            }
        } catch (error) {
            const apiMessage =
                error?.response?.data?.message ||
                error?.response?.data?.error;

            setSnackbarMessage(apiMessage || en_ja["Something went wrong"]);
            setSnackbarOpen(true);
            setSnackbarType("error");
        } finally {
            setLoading(false);
        }
    };


    return (
        <Dialog open={open} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pb: 1 }}>
                <Box display="flex" justifyContent="space-between">
                    <Box>
                        <Typography variant="h6">
                            {editMode
                                ? en_ja["Update Admin"]
                                : en_ja["Create New Admin"]}
                        </Typography>

                        {!editMode && (
                            <Typography
                                variant="body2"
                                sx={{ color: "text.secondary", mt: 0.5 }}
                            >
                                {
                                    en_ja[
                                    "Add a new user and assign them a role. Click save when you're done."
                                    ]
                                }
                            </Typography>
                        )}
                    </Box>

                    <IconButton
                        onClick={handleClose}
                        sx={{
                            mt: "-4px",
                            borderRadius: "50%",
                            width: 36,
                            height: 36,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "background-color 0.2s ease",

                            "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.08)",
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>

                <TextField
                    label={en_ja["Last Name"]}
                    name="last_name"
                    fullWidth
                    value={formData.last_name}
                    onChange={handleChange}
                    margin="normal"
                    error={!!errors.last_name}
                    helperText={errors.last_name}
                />

                <TextField
                    label={en_ja["First Name"]}
                    name="first_name"
                    fullWidth
                    value={formData.first_name}
                    onChange={handleChange}
                    margin="normal"
                    error={!!errors.first_name}
                    helperText={errors.first_name}
                />

                <TextField
                    label={en_ja["User Name"]}
                    name="name"
                    fullWidth
                    value={formData.name}
                    onChange={handleChange}
                    margin="normal"
                    error={!!errors.name}
                    helperText={errors.name}
                    disabled={editMode}
                />

                <TextField
                    label={en_ja["Email"]}
                    name="email"
                    fullWidth
                    value={formData.email}
                    onChange={handleChange}
                    margin="normal"
                    error={!!errors.email}
                    helperText={errors.email}
                    disabled={editMode}
                />

                {!editMode && (
                    <TextField
                        label={en_ja["Password"]}
                        name="password"
                        type={showPassword ? "text" : "password"}
                        fullWidth
                        value={formData.password}
                        onChange={handleChange}
                        margin="normal"
                        error={!!errors.password}
                        helperText={errors.password}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                        size="small"
                                    >
                                        {showPassword ? (
                                            <VisibilityOff fontSize="small" />
                                        ) : (
                                            <Visibility fontSize="small" />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                )}

                {!editMode && (
                    <Box mt={2}>
                        <TextField
                            select
                            label={en_ja["Role"]}
                            name="role"
                            fullWidth
                            value={formData.role}
                            onChange={handleChange}
                            error={!!errors.role}
                        >
                            {roles.map((role) => (
                                <MenuItem key={role} value={role}>
                                    {role}
                                </MenuItem>
                            ))}
                        </TextField>

                        {errors.role && (
                            <Typography
                                variant="caption"
                                color="error"
                                sx={{ ml: 1.5 }}
                            >
                                {errors.role}
                            </Typography>
                        )}
                    </Box>
                )}

            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
                <Button onClick={handleClose} variant="outlined">
                    {en_ja["Cancel"]}
                </Button>

                <LoadingButton
                    onClick={handleSubmit}
                    loading={loading}
                    variant="contained"
                    sx={{ backgroundColor: "rgba(220, 53, 69, 1)" }}
                    disabled={editMode && !hasChanges}
                >
                    {editMode ? en_ja["Update"] : en_ja["Save"]}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default CreateNewAdmin;
